import { Component } from '@theme/component';
import {
  center,
  closest,
  clamp,
  mediaQueryLarge,
  prefersReducedMotion,
  preventDefault,
  viewTransition,
  scheduler,
} from '@theme/utilities';
import { Scroller, scrollIntoView } from '@theme/scrolling';
import { SlideshowSelectEvent } from '@theme/events';

// The threshold for determining visibility of slides.
const SLIDE_VISIBLITY_THRESHOLD = 0.7;

/**
 * Slideshow custom element that allows sliding between content.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} scroller
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLElement} [current]
 * @property {HTMLElement[]} [thumbnails]
 * @property {HTMLElement[]} [dots]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class Slideshow extends Component {
  static get observedAttributes() {
    return ['initial-slide'];
  }

  /**
   * @param {string} name
   * @param {string} oldValue
   * @param {string} newValue
   */
  attributeChangedCallback(name, oldValue, newValue) {
    // Collection page filtering will Morph slideshow galleries in place, updating
    // the slideshow[initial-slide] and slideshow-slide[hidden] attributes.
    // We need to re-select() the slide after the morph is complete, but not before
    // slideshow-slide elements have their [hidden] attribute updated.
    if (name === 'initial-slide' && oldValue !== newValue) {
      queueMicrotask(() => {
        // Only select if the component is connected and initialized
        if (!this.isConnected || !this.#scroll || !this.refs.slides) return;
        const index = parseInt(newValue, 10) || 0;
        const slide_id = this.refs.slides[index]?.getAttribute('slide-id');
        if (slide_id) {
          this.select({ id: slide_id }, undefined, { animate: false });
        }
      });
    }
  }

  requiredRefs = ['scroller'];

  async connectedCallback() {
    super.connectedCallback();

    // Wait for any in-progress view transitions to finish
    if (viewTransition.current) {
      await viewTransition.current;
      // It's possible that the slideshow was disconnected before the view transition finished
      if (!this.isConnected) return;
    }

    const slideCount = this.slides?.length || 0;
    slideCount <= 1 ? this.#setupSlideshowWithoutControls() : this.#setupSlideshow();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('scroll', this.#handleScroll);

    if (this.#scroll) {
      const { scroller } = this.refs;
      scroller.removeEventListener('mousedown', this.#handleMouseDown);
      this.#scroll.destroy();
    }

    const slideCount = this.slides?.length || 0;
    if (slideCount > 1) {
      this.removeEventListener('mouseenter', this.suspend);
      this.removeEventListener('mouseleave', this.resume);
      this.removeEventListener('pointerenter', this.#handlePointerEnter);
      document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    }

    this.#removePdpWheelListener();

    if (this.#wheelUnlockTimer) {
      clearTimeout(this.#wheelUnlockTimer);
      this.#wheelUnlockTimer = undefined;
    }
    if (this.#viewportLayoutSyncTimeout !== undefined) {
      clearTimeout(this.#viewportLayoutSyncTimeout);
      this.#viewportLayoutSyncTimeout = undefined;
    }
    this.#wheelLocked = false;
    this.#wheelDelta = 0;
    this.#lastWheelTimestamp = 0;

    if (this.#resizeObserver) {
      this.#resizeObserver.disconnect();
    }

    if (this.#intersectionObserver) {
      this.#intersectionObserver.disconnect();
      this.#intersectionObserver = null;
    }

    this.#pdpScrollSyncGeneration += 1;
    this.#releasePdpScrollSyncLock();
    this.#flushInitCompleteWaiters();
    this.#initComplete = true;
  }

  /** Indicates whether the slideshow is nested inside another slideshow. */
  get isNested() {
    return this.parentElement?.closest('slideshow-component') !== null;
  }

  get initialSlide() {
    return this.refs.slides?.[this.initialSlideIndex];
  }

  /**
   * Selects a slide based on the input index.
   * @param {number|string|{id: string}} input - The index or id of the slide to select.
   * @param {Event} [event] - The event that triggered the selection.
   * @param {Object} [options] - The options for the selection.
   * @param {boolean} [options.animate=true] - Whether to animate the selection.
   * @param {boolean} [options.scrollViewport] - Whether viewport-mode selections can scroll the page.
   */
  async select(input, event, options = {}) {
    if (this.#disabled || !this.refs.slides?.length) return;
    if (!this.#scroll) return;

    for (const slide of this.refs.slides) {
      if (slide.hasAttribute('reveal')) {
        slide.removeAttribute('reveal');
        slide.setAttribute('aria-hidden', 'true');
      }
    }

    // Figure out the raw desired index (could be -1 if user is on first slide and clicks prev)
    let requestedIndex = (() => {
      if (typeof input === 'number') return input;
      if (typeof input === 'string') return parseInt(input, 10);
      if ('id' in input) {
        const requestedSlide = this.refs.slides.find((slide) => slide.getAttribute('slide-id') == input.id);

        if (!requestedSlide || !this.slides) return;

        // Force the slide to be revealed if it is hidden
        if (requestedSlide.hasAttribute('hidden')) {
          requestedSlide.setAttribute('reveal', '');
          requestedSlide.setAttribute('aria-hidden', 'false');
        }

        let idx = this.slides.indexOf(requestedSlide);
        if (idx === -1 && requestedSlide.hasAttribute('hidden')) {
          requestedSlide.removeAttribute('hidden');
          idx = this.slides.indexOf(requestedSlide);
        }

        return idx;
      }
    })();

    const isIdSelection = typeof input === 'object' && input !== null && 'id' in input;

    const { slides } = this;

    if (slides?.length && this.current >= slides.length) {
      this.current = Math.max(0, slides.length - 1);
    }

    const current = this.current;
    const currentSlide = slides?.[current];

    // Guard checks: no slides, invalid index, or selecting the same slide
    if (!slides?.length || requestedIndex === undefined || isNaN(requestedIndex)) return;

    // id-based selection: -1 means the target slide is not in the visible list — do not treat as
    // "previous from first" and jump to the last slide (breaks combined-listing / injected slides).
    if (isIdSelection && requestedIndex < 0) return;

    const requestedSlideElement = slides?.[requestedIndex];
    if (currentSlide === requestedSlideElement) {
      // Variant gallery replace still needs the lock when select() is a no-op (already on slide 0).
      if (!event && this.#isPdpMediaGallery && requestedSlideElement instanceof HTMLElement) {
        this.#lockPdpScrollSyncUntilSettled(requestedSlideElement);
      }
      return;
    }

    if (!this.infinite) requestedIndex = clamp(requestedIndex, 0, slides.length - 1);

    event?.preventDefault();

    const { animate = true, scrollViewport = event != null } = options;
    const lastIndex = slides.length - 1;

    // Decide the actual target index (clamp for infinite loop)
    let index = requestedIndex;
    if (requestedIndex < 0) index = lastIndex;
    else if (requestedIndex > lastIndex) index = 0;

    const isAdjacentSlide = Math.abs(index - current) <= 1 && requestedIndex >= 0 && requestedIndex <= lastIndex;
    const { visibleSlides } = this;
    const instant = prefersReducedMotion() || !animate;
    const isWrapping = requestedIndex < 0 || requestedIndex > lastIndex;
    const usePlaceholder =
      !this.#useViewportScroll &&
      !instant &&
      !isAdjacentSlide &&
      (visibleSlides.length === 1 || (this.infinite && isWrapping));

    if (usePlaceholder) {
      this.#disabled = true;
      await this.#scroll.finished;

      const targetSlide = slides[index];
      if (!targetSlide || !currentSlide) {
        this.#disabled = false;
        return;
      }

      const placeholder = document.createElement('slideshow-slide');
      targetSlide.before(placeholder);

      if (requestedIndex < current) {
        currentSlide.before(targetSlide);
      } else {
        currentSlide.after(targetSlide);
      }

      if (current === 0) this.#scroll.to(currentSlide, { instant: true });

      const previousIndex = this.current;
      targetSlide.setAttribute('aria-hidden', 'false');

      if (this.#scroll) {
        this.#scroll.to(targetSlide, { instant: false });
      }

      queueMicrotask(async () => {
        try {
          await this.#scroll.finished;
          placeholder.replaceWith(targetSlide);
          this.#scroll.to(targetSlide, { instant: true });
          this.current = Math.max(0, this.slides?.indexOf(targetSlide) ?? -1);
          this.#updateScrollerHeightForVerticalMode();
          this.#updateScrollTrackingMode();
          this.#centerSelectedThumbnail(index, 'instant');
          this.dispatchEvent(
            new SlideshowSelectEvent({
              index,
              previousIndex,
              userInitiated: event != null,
              trigger: 'select',
              slide: targetSlide,
              id: targetSlide.getAttribute('slide-id'),
            })
          );
        } finally {
          this.#disabled = false;
        }
      });
      return;
    }

    const slide = slides[index];
    if (!slide) return;

    if (!event && this.#isPdpMediaGallery) {
      this.#lockPdpScrollSyncUntilSettled(slide); // programmatic PDP select only
    }

    const previousIndex = this.current;

    slide.setAttribute('aria-hidden', 'false');

    if (this.#useViewportScroll) {
      if (!scrollViewport) {
        this.current = Math.max(0, this.slides?.indexOf(slide) ?? -1);
        this.#updateScrollerHeightForVerticalMode();
        this.#updateScrollTrackingMode();
        this.#centerSelectedThumbnail(index, instant ? 'instant' : 'smooth');
        this.dispatchEvent(
          new SlideshowSelectEvent({
            index,
            previousIndex,
            userInitiated: event != null,
            trigger: 'select',
            slide,
            id: slide.getAttribute('slide-id'),
          })
        );
        return;
      }
      slide.scrollIntoView({ behavior: instant ? 'instant' : 'smooth', block: 'center' });
      this.current = Math.max(0, this.slides?.indexOf(slide) ?? -1);
      if (instant) {
        this.#updateScrollerHeightForVerticalMode();
        this.#updateScrollTrackingMode();
      } else {
        this.#deferScrollerLayoutSyncAfterViewportScroll();
      }
      this.#centerSelectedThumbnail(index, instant ? 'instant' : 'smooth');
      this.dispatchEvent(
        new SlideshowSelectEvent({
          index,
          previousIndex,
          userInitiated: event != null,
          trigger: 'select',
          slide,
          id: slide.getAttribute('slide-id'),
        })
      );
      return;
    }

    if (this.#scroll) {
      this.#scroll.to(slide, { instant });
    }

    this.current = Math.max(0, this.slides?.indexOf(slide) ?? -1);
    this.#updateScrollerHeightForVerticalMode();
    this.#updateScrollTrackingMode();

    this.#centerSelectedThumbnail(index, instant ? 'instant' : 'smooth');

    this.dispatchEvent(
      new SlideshowSelectEvent({
        index,
        previousIndex,
        userInitiated: event != null,
        trigger: 'select',
        slide,
        id: slide.getAttribute('slide-id'),
      })
    );
  }

  /**
   * Advances to the next slide.
   * @param {Event} [event] - The event that triggered the next slide.
   * @param {{ animate?: boolean, scrollViewport?: boolean }} [options]
   */
  next(event, options) {
    event?.preventDefault();
    this.select(this.nextIndex, event, options);
  }

  /**
   * Goes back to the previous slide.
   * @param {Event} [event] - The event that triggered the previous slide.
   * @param {{ animate?: boolean, scrollViewport?: boolean }} [options]
   */
  previous(event, options) {
    event?.preventDefault();
    this.select(this.previousIndex, event, options);
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (this.#interval) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(value) {
    if (value) {
      this.setAttribute('paused', '');
    } else {
      this.removeAttribute('paused');
    }
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`£{interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  /**
   * The current slide index.
   * @type {number}
   */
  #current = 0;

  get current() {
    return this.#current;
  }

  /**
   * Sets the current slide index and update the DOM
   * @type {number}
   */
  set current(value) {
    const { current, thumbnails, dots, slides, previous, next } = this.refs;

    this.#current = value;

    const progressFills = this.querySelectorAll('.slideshow-controls__progress-fill');
    if (progressFills.length > 0) {
      this.style.setProperty('--slideshow-current', `£{value}`);
      for (const progressFill of progressFills) {
        if (progressFill instanceof HTMLElement) {
          progressFill.style.setProperty('--slideshow-current', `£{value}`);
        }
      }
    }

    if (current) current.textContent = `£{value + 1}`;

    for (const controls of [thumbnails, dots]) {
      controls?.forEach((el, i) => el.setAttribute('aria-selected', `£{i === value}`));
    }

    if (previous) previous.disabled = Boolean(!this.infinite && value === 0);
    if (next) next.disabled = Boolean(!this.infinite && slides && this.nextIndex >= slides.length);
  }

  get infinite() {
    return this.getAttribute('infinite') != null;
  }

  get visibleSlides() {
    return this.#visibleSlides.filter(
      (slide) => slide.isConnected && (!slide.hasAttribute('hidden') || slide.hasAttribute('reveal'))
    );
  }

  get #isCardGallery() {
    return Boolean(this.closest('product-card') && !this.#isPdpMediaGallery);
  }

  get #slideStep() {
    if (this.#isCardGallery || this.infinite) return 1;
    const { visibleSlides } = this;
    return visibleSlides.length > 1 ? visibleSlides.length : 1;
  }

  get previousIndex() {
    return this.current - this.#slideStep;
  }

  get nextIndex() {
    return this.current + this.#slideStep;
  }

  get atStart() {
    const { current, slides } = this;

    return slides?.length ? current === 0 : false;
  }

  get atEnd() {
    const { current, slides } = this;

    return slides?.length ? current === slides.length - 1 : false;
  }

  /**
   * Sets the disabled attribute.
   * @param {boolean} value - The value to set the disabled attribute to.
   */
  set disabled(value) {
    this.setAttribute('disabled', String(value));
  }
  /**
   * Whether the slideshow is disabled.
   * @type {boolean}
   */
  get disabled() {
    return (
      this.getAttribute('disabled') === 'true' || (this.hasAttribute('mobile-disabled') && !mediaQueryLarge.matches)
    );
  }

  /**
   * Indicates whether the slideshow is temporarily disabled (e.g., during infinite loop transition).
   * @type {boolean}
   */
  #disabled = false;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  /**
   * Wheel delta accumulator for vertical slide navigation.
   * @type {number}
   */
  #wheelDelta = 0;

  /**
   * Timestamp of the last wheel event.
   * @type {number}
   */
  #lastWheelTimestamp = 0;

  /**
   * Prevents multiple slide jumps per wheel gesture.
   * @type {boolean}
   */
  #wheelLocked = false;

  /**
   * Timer used to release wheel lock.
   * @type {number|undefined}
   */
  #wheelUnlockTimer = undefined;

  /**
   * @type {number|undefined}
   */
  #viewportLayoutSyncTimeout = undefined;

  /**
   * Options used when registering the PDP wheel listener (required for matching removeEventListener).
   * @type {AddEventListenerOptions | undefined}
   */
  #pdpWheelListenerOptions = undefined;

  /**
   * The Scroller instance that manages scrolling.
   * @type {Scroller}
   */
  #scroll;

  /**
   * The ResizeObserver instance for monitoring scroller size changes
   * @type {ResizeObserver}
   */
  #resizeObserver;

  /**
   * Track if slideshow selection should follow viewport scrolling
   * instead of internal scroller scrolling.
   * @type {boolean}
   */
  #useViewportScroll = false;

  /**
   * Enables PDP-only vertical slideshow behavior.
   * @type {boolean}
   */
  #isPdpMediaGallery = false;

  /**
   * IntersectionObserver for efficient visibility tracking of slides
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * Cached visible slides result from IntersectionObserver
   * @type {HTMLElement[]}
   */
  #visibleSlides = [];

  // PDP variant swaps: block #handleScroll from overriding programmatic select(0) while the
  // featured image loads and slide height settles (was jumping to slide 1 ~100ms later).
  #pdpScrollSyncLocked = false;

  /** @type {number | undefined} */
  #pdpScrollSyncUnlockTimer;

  /** @type {number} */
  #pdpScrollSyncGeneration = 0;

  // media-gallery selects after the first rAF init select on a freshly replaced gallery.
  #initComplete = false;

  /** @type {Array<() => void>} */
  #initCompleteWaiters = [];

  whenInitComplete() {
    if (this.#initComplete) return Promise.resolve();

    return /** @type {Promise<void>} */ (
      new Promise((resolve) => {
        this.#initCompleteWaiters.push(resolve);
      })
    );
  }

  #resolveInitComplete() {
    if (this.#initComplete) return;

    this.#initComplete = true;

    for (const resolve of this.#initCompleteWaiters) {
      resolve();
    }

    this.#initCompleteWaiters = [];
  }

  #flushInitCompleteWaiters() {
    const waiters = this.#initCompleteWaiters;
    this.#initCompleteWaiters = [];

    for (const resolve of waiters) {
      resolve();
    }
  }

  #releasePdpScrollSyncLock() {
    this.#pdpScrollSyncLocked = false;

    if (this.#pdpScrollSyncUnlockTimer) {
      clearTimeout(this.#pdpScrollSyncUnlockTimer);
      this.#pdpScrollSyncUnlockTimer = undefined;
    }
  }

  /**
   * @param {HTMLElement} slide
   */
  #lockPdpScrollSyncUntilSettled(slide) {
    if (!this.#isPdpMediaGallery) return;

    this.#pdpScrollSyncGeneration += 1;
    const generation = this.#pdpScrollSyncGeneration;

    if (this.#pdpScrollSyncUnlockTimer) {
      clearTimeout(this.#pdpScrollSyncUnlockTimer);
      this.#pdpScrollSyncUnlockTimer = undefined;
    }

    this.#pdpScrollSyncLocked = true;

    const unlock = () => {
      if (generation !== this.#pdpScrollSyncGeneration) return;

      requestAnimationFrame(() => {
        if (generation !== this.#pdpScrollSyncGeneration) return;

        requestAnimationFrame(() => {
          if (generation !== this.#pdpScrollSyncGeneration) return;

          this.#releasePdpScrollSyncLock();
        });
      });
    };

    const waitForLayout = () => {
      if (generation !== this.#pdpScrollSyncGeneration) return;

      let lastHeight = slide.offsetHeight;
      let stableFrames = 0;

      const tick = () => {
        if (generation !== this.#pdpScrollSyncGeneration) return;

        const height = slide.offsetHeight;

        if (height > 0 && height === lastHeight) {
          stableFrames += 1;
        } else {
          stableFrames = 0;
          lastHeight = height;
        }

        if (stableFrames >= 3) {
          const targetIndex = this.slides?.indexOf(slide) ?? 0;
          const synced = this.#sync();

          if (synced !== targetIndex && this.#scroll) {
            this.#scroll.to(slide, { instant: true });
            this.current = targetIndex;
            stableFrames = 0;
            requestAnimationFrame(tick);
            return;
          }

          unlock();
          return;
        }

        requestAnimationFrame(tick);
      };

      requestAnimationFrame(tick);
    };

    this.#pdpScrollSyncUnlockTimer = window.setTimeout(() => {
      if (generation !== this.#pdpScrollSyncGeneration) return;

      this.#releasePdpScrollSyncLock();
    }, 2500);

    const image = slide.querySelector('.product-media__image');

    if (!(image instanceof HTMLImageElement) || image.complete) {
      waitForLayout();
      return;
    }

    image.addEventListener('load', waitForLayout, { once: true });
    image.addEventListener('error', waitForLayout, { once: true });
  }

  /**
   * Setup the slideshow without controls for zero or one slides
   */
  #setupSlideshowWithoutControls() {
    this.current = 0;
    if (this.hasAttribute('auto-hide-controls')) {
      const { slideshowControls } = this.refs;
      if (slideshowControls instanceof HTMLElement) {
        slideshowControls.hidden = true;
      }
    }

    if (this.refs.slides?.[0]) {
      this.refs.slides[0].setAttribute('aria-hidden', 'false');
    }

    this.#resolveInitComplete();
  }

  /**
   * Setup the slideshow with controls for when there are multiple slides
   */
  #setupSlideshow() {
    // Setup IntersectionObserver first for efficient visibility tracking
    this.#setupIntersectionObserver();

    // Setup the scroll instance
    const { scroller } = this.refs;
    this.#isPdpMediaGallery =
      this.hasAttribute('data-pdp-media-gallery') ||
      this.classList.contains('slideshow--pdp-media-gallery') ||
      this.closest('media-gallery.media-gallery--pdp') !== null;

    this.#scroll = new Scroller(scroller, {
      onScroll: this.#handleScroll,
      onScrollStart: this.#onTransitionInit,
      onScrollEnd: this.#onTransitionEnd,
    });

    scroller.addEventListener('mousedown', this.#handleMouseDown);

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    this.#syncPdpWheelListener();
    this.addEventListener('pointerenter', this.#handlePointerEnter);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.#updateScrollerHeightForVerticalMode();
    this.#updateScrollTrackingMode();
    this.#updateControlsVisibility();

    this.disabled = this.isNested || this.disabled;

    this.resume();

    this.current = this.initialSlideIndex;

    // Batch reads and writes to the DOM
    scheduler.schedule(() => {
      // Wait for next frame to ensure layout is fully calculated before setting initial scroll position
      // This prevents race conditions on Safari mobile when section_width is 'full-width'
      requestAnimationFrame(() => {
        const index = this.initialSlideIndex;
        const initialSlideId = this.initialSlide?.getAttribute('slide-id');
        const selectOptions = { animate: false, scrollViewport: false };

        if (initialSlideId) {
          this.select({ id: initialSlideId }, undefined, selectOptions);
        } else {
          this.select(index, undefined, selectOptions);
        }

        this.#resolveInitComplete();

        if ((this.slides?.length ?? 0) > 1) {
          this.#updateVisibleSlides();
        }
      });

      this.#resizeObserver = new ResizeObserver(async () => {
        if (viewTransition.current) await viewTransition.current;
        this.#updateScrollerHeightForVerticalMode();
        this.#updateScrollTrackingMode();

        if ((this.slides?.length ?? 0) > 1) {
          this.#updateVisibleSlides();
        }

        if (this.hasAttribute('auto-hide-controls')) {
          this.#updateControlsVisibility();
        }
      });

      this.#resizeObserver.observe(this.refs.slideshowContainer);
    });
  }

  /**
   * Callback invoked on user initiated scroll to sync the current slide index
   * and emit a slide change event if the index has changed.
   */
  #handleScroll = () => {
    if (this.#pdpScrollSyncLocked) return; // see #pdpScrollSyncLocked

    const previousIndex = this.#current;
    const index = this.#sync();

    if (index === previousIndex) return;

    const slide = this.slides?.[index];
    if (!slide) return;

    this.dispatchEvent(
      new SlideshowSelectEvent({
        index,
        previousIndex,
        userInitiated: true,
        trigger: 'scroll',
        slide,
        id: slide.getAttribute('slide-id'),
      })
    );
  };

  #onTransitionInit = () => {
    this.setAttribute('transitioning', '');
  };

  #onTransitionEnd = () => {
    this.#updateVisibleSlides();
    this.#updateScrollerHeightForVerticalMode();
    this.#updateScrollTrackingMode();
    this.removeAttribute('transitioning');
  };

  /**
   * Synchronizes the scroll position and updates the current slide index.
   * @returns {number} The index of the current slide.
   */
  #sync = () => {
    const { slides } = this;
    if (!slides) return (this.current = 0);

    if (!this.#scroll) return (this.current = 0);

    if (this.#isPdpMediaGallery && this.#useViewportScroll) {
      const centers = slides.map((slide) => center(slide, 'y'));
      const referencePoint = window.innerHeight / 2;
      const closestCenter = closest(centers, referencePoint);
      const index = centers.indexOf(closestCenter);

      return (this.current = index < 0 ? 0 : index);
    }

    const { axis } = this.#scroll;
    const { scroller } = this.refs;
    const visibleSlides = this.visibleSlides;
    let candidateSlides;
    if (visibleSlides.length) {
      candidateSlides = visibleSlides;
    } else if (this.#isPdpMediaGallery) {
      candidateSlides = slides;
    } else {
      return this.current;
    }
    const centers = candidateSlides.map((slide) => center(slide, axis));
    const referencePoint =
      candidateSlides.length > 1 ? scroller.getBoundingClientRect()[axis] : center(scroller, axis);
    const closestCenter = closest(centers, referencePoint);
    const closestVisibleSlide = candidateSlides[centers.indexOf(closestCenter)];

    if (!closestVisibleSlide) return (this.current = 0);

    const index = slides.indexOf(closestVisibleSlide);

    return (this.current = index);
  };

  #dragging = false;

  /**
   * Handles the 'mousedown' event to start dragging slides.
   * @param {MouseEvent} event - The mousedown event.
   */
  #handleMouseDown = (event) => {
    const { slides } = this;

    if (!slides || slides.length <= 1) return;
    if (!(event.target instanceof Element)) return;
    if (this.disabled || this.#dragging) return;

    // Check if the event target is within a 3D model interactive element
    // This prevents the slideshow from capturing drag events when interacting with 3D models
    if (event.target.closest('model-viewer')) {
      return;
    }

    event.preventDefault();
    // Store initial position but don't start handling yet
    const { axis } = this.#scroll;
    const startPosition = event[axis];

    const controller = new AbortController();
    const { signal } = controller;
    const startTime = performance.now();
    let previous = startPosition;
    let velocity = 0;
    let moved = false;
    let distanceTravelled = 0;

    this.#dragging = true;

    /**
     * Handles the 'pointermove' event to update the scroll position.
     * @param {PointerEvent} event - The pointermove event.
     */
    const onPointerMove = (event) => {
      const current = event[axis];
      const initialDelta = startPosition - current;

      if (!initialDelta) return;

      if (!moved) {
        moved = true;
        this.setPointerCapture(event.pointerId);

        // Prevent clicks once the user starts dragging
        document.addEventListener('click', preventDefault, { once: true, signal });

        const movingRight = initialDelta < 0;
        const movingLeft = initialDelta > 0;

        // Check if the current slideshow should handle this drag
        const closestSlideshow = this.parentElement?.closest('slideshow-component');
        const isNested = closestSlideshow instanceof Slideshow && closestSlideshow !== this;
        const cannotMoveInDirection = (movingRight && this.atStart) || (movingLeft && this.atEnd);

        // Abort and let the parent slideshow handle the drag if we're moving in a direction where nested slideshow can't move
        if (isNested && cannotMoveInDirection) {
          controller.abort();
          return;
        }

        this.pause();
        this.setAttribute('dragging', '');
      }

      // Stop the event from bubbling up to parent slideshow components
      event.stopImmediatePropagation();

      const delta = previous - current;
      const timeDelta = performance.now() - startTime;
      velocity = Math.round((delta / timeDelta) * 1000);
      previous = current;
      distanceTravelled += Math.abs(delta);

      this.#scroll.by(delta, { instant: true });
    };

    /**
     * Handles the 'pointerup' event to stop dragging slides.
     * @param {PointerEvent} event - The pointerup event.
     */
    const onPointerUp = async (event) => {
      controller.abort();
      const { current, slides } = this;
      const { scroller } = this.refs;

      this.#dragging = false;

      if (!slides?.length || !scroller) return;

      const direction = Math.sign(velocity);
      const next = this.#sync();

      const modifier = current !== next || Math.abs(velocity) < 10 || distanceTravelled < 10 ? 0 : direction;
      const newIndex = clamp(next + modifier, 0, slides.length - 1);

      const newSlide = slides[newIndex];
      const currentIndex = this.current;

      if (!newSlide) throw new Error(`Slide not found at index £{newIndex}`);

      this.#scroll.to(newSlide);

      this.removeAttribute('dragging');
      this.releasePointerCapture(event.pointerId);

      this.#centerSelectedThumbnail(newIndex);

      this.dispatchEvent(
        new SlideshowSelectEvent({
          index: newIndex,
          previousIndex: currentIndex,
          userInitiated: true,
          trigger: 'drag',
          slide: newSlide,
          id: newSlide.getAttribute('slide-id'),
        })
      );

      this.current = newIndex;

      await this.#scroll.finished;

      // It's possible that the user started dragging again before the scroll finished
      if (this.#dragging) return;

      this.#scroll.snap = true;
      this.resume();
    };

    this.#scroll.snap = false;

    document.addEventListener('pointermove', onPointerMove, { signal });
    document.addEventListener('pointerup', onPointerUp, { signal });
    /**
     * pointerDown calls onPointerUp to fix an issue where the first tap-and-drag
     * on the zoom dialog is captured by the pointerMove/pointerUp listeners,
     * sometimes causing the slideshow to change slides unexpectedly
     */
    document.addEventListener('pointerdown', onPointerUp, { signal });
    document.addEventListener('pointercancel', onPointerUp, { signal });
    document.addEventListener('pointercapturelost', onPointerUp, { signal });
  };

  #handlePointerEnter = () => {
    this.setAttribute('actioned', '');
  };

  /**
   * Move vertical slides on wheel while hovering the media.
   * @param {WheelEvent} event
   */
  #handleWheel = (event) => {
    if (!this.#isPdpMediaGallery) return;
    if (!this.#scroll || this.disabled) return;
    if (!this.matches(':hover')) return;

    const { scroller } = this.refs;
    if (!scroller) return;

    const isVertical = this.#scroll.axis === 'y';
    const hasInternalVerticalOverflow = scroller.scrollHeight > scroller.clientHeight + 1;

    if (!isVertical) return;
    // Prefer native wheel scrolling when the slideshow can scroll internally.
    // This gives a page-like, pixel-based feel instead of full-slide jumps.
    if (hasInternalVerticalOverflow) return;
    if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;

    event.preventDefault();

    const now = performance.now();
    if (now - this.#lastWheelTimestamp > 180) {
      this.#wheelDelta = 0;
    }
    this.#lastWheelTimestamp = now;

    const normalizedDeltaY =
      event.deltaMode === WheelEvent.DOM_DELTA_LINE
        ? event.deltaY * 16
        : event.deltaMode === WheelEvent.DOM_DELTA_PAGE
          ? event.deltaY * window.innerHeight
          : event.deltaY;

    this.#wheelDelta += normalizedDeltaY;

    if (this.#wheelLocked || this.hasAttribute('transitioning')) return;

    // Require a meaningful gesture distance to emulate natural page scrolling.
    const threshold = Math.max(220, scroller.clientHeight * 0.2);
    if (Math.abs(this.#wheelDelta) < threshold) return;

    const direction = Math.sign(this.#wheelDelta);
    this.#wheelDelta = 0;
    this.#wheelLocked = true;

    if (direction > 0) {
      this.next(undefined, { animate: true, scrollViewport: true });
    } else if (direction < 0) {
      this.previous(undefined, { animate: true, scrollViewport: true });
    }

    if (this.#wheelUnlockTimer) {
      clearTimeout(this.#wheelUnlockTimer);
    }

    const scheduleWheelUnlock = () => {
      this.#wheelUnlockTimer = window.setTimeout(() => {
        this.#wheelLocked = false;
        this.#wheelUnlockTimer = undefined;
      }, 140);
    };

    if (this.#useViewportScroll) {
      const settleAfterScrollIntoViewMs = prefersReducedMotion() ? 0 : 450;
      this.#wheelUnlockTimer = window.setTimeout(scheduleWheelUnlock, settleAfterScrollIntoViewMs);
    } else {
      void this.#scroll.finished.then(scheduleWheelUnlock);
    }
  };

  get slides() {
    return this.refs.slides?.filter((slide) => !slide.hasAttribute('hidden') || slide.hasAttribute('reveal'));
  }

  /**
   * The initial slide index.
   * @type {number}
   */
  get initialSlideIndex() {
    const initialSlide = this.getAttribute('initial-slide');
    if (initialSlide == null) return 0;

    return parseInt(initialSlide, 10);
  }

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());

  #updateScrollerHeightForVerticalMode() {
    if (!this.#scroll) return;

    const { scroller } = this.refs;
    if (!scroller) return;
    if (!this.#isPdpMediaGallery) {
      scroller.style.removeProperty('height');
      return;
    }

    const isVertical = this.#scroll.axis === 'y';

    if (!isVertical) {
      scroller.style.removeProperty('height');
      return;
    }

    const slides = this.slides?.filter((slide) => slide instanceof HTMLElement) || [];
    if (!slides.length) return;

    // Keep a stable gallery height across media changes by using the tallest slide.
    const tallestSlideHeight = slides.reduce((max, slide) => Math.max(max, slide.offsetHeight), 0);
    if (!tallestSlideHeight) return;

    scroller.style.height = `£{tallestSlideHeight}px`;
  }

  #updateScrollTrackingMode() {
    if (!this.#scroll) return;

    const { scroller } = this.refs;
    if (!scroller) return;
    if (!this.#isPdpMediaGallery) {
      if (this.#useViewportScroll) {
        window.removeEventListener('scroll', this.#handleScroll);
      }
      this.#useViewportScroll = false;
      this.#syncPdpWheelListener();
      return;
    }

    const isVertical = this.#scroll.axis === 'y';
    const hasInternalVerticalOverflow = scroller.scrollHeight > scroller.clientHeight + 1;
    const shouldUseViewportScroll = mediaQueryLarge.matches && isVertical && !hasInternalVerticalOverflow;

    if (shouldUseViewportScroll !== this.#useViewportScroll) {
      this.#useViewportScroll = shouldUseViewportScroll;

      if (this.#useViewportScroll) {
        window.addEventListener('scroll', this.#handleScroll, { passive: true });
      } else {
        window.removeEventListener('scroll', this.#handleScroll);
      }
    }

    this.#syncPdpWheelListener();
  }

  #deferScrollerLayoutSyncAfterViewportScroll() {
    if (this.#viewportLayoutSyncTimeout !== undefined) {
      clearTimeout(this.#viewportLayoutSyncTimeout);
    }
    this.#viewportLayoutSyncTimeout = window.setTimeout(() => {
      this.#viewportLayoutSyncTimeout = undefined;
      if (!this.isConnected) return;
      this.#updateScrollerHeightForVerticalMode();
      this.#updateScrollTrackingMode();
    }, 500);
  }

  #removePdpWheelListener() {
    if (this.#pdpWheelListenerOptions) {
      this.removeEventListener('wheel', this.#handleWheel, this.#pdpWheelListenerOptions);
      this.#pdpWheelListenerOptions = undefined;
    }
  }

  /**
   * Passive when the gallery delegates wheel to native internal scroll; non-passive only when slide
   * stepping may call preventDefault.
   */
  #syncPdpWheelListener() {
    this.#removePdpWheelListener();
    if (!this.#isPdpMediaGallery || !this.#scroll) return;

    const { scroller } = this.refs;
    if (!scroller) return;

    const isVertical = this.#scroll.axis === 'y';
    const hasInternalVerticalOverflow = scroller.scrollHeight > scroller.clientHeight + 1;
    const mayCallPreventDefault = isVertical && !hasInternalVerticalOverflow;
    const options = { passive: !mayCallPreventDefault };

    this.addEventListener('wheel', this.#handleWheel, options);
    this.#pdpWheelListenerOptions = options;
  }

  #updateControlsVisibility() {
    if (!this.hasAttribute('auto-hide-controls')) return;

    const { scroller, slideshowControls } = this.refs;

    if (!(slideshowControls instanceof HTMLElement)) return;

    slideshowControls.hidden = scroller.scrollWidth <= scroller.offsetWidth;
  }

  /**
   * Setup IntersectionObserver for efficient visibility tracking of slides
   */
  #setupIntersectionObserver() {
    const { slides, scroller } = this.refs;
    if (!slides?.length) return;

    if (this.#intersectionObserver) {
      this.#intersectionObserver.disconnect();
    }

    this.#visibleSlides = [];

    this.#intersectionObserver = new IntersectionObserver(
      (entries) => {
        const allEntries = [
          ...entries,
          ...(this.#intersectionObserver ? this.#intersectionObserver.takeRecords() : []),
        ];

        for (const entry of allEntries) {
          const slide = /** @type {HTMLElement} */ (entry.target);
          const isCurrentlyVisible = this.#visibleSlides.includes(slide);
          const shouldBeVisible = entry.intersectionRatio >= SLIDE_VISIBLITY_THRESHOLD;

          if (shouldBeVisible && !isCurrentlyVisible) {
            this.#visibleSlides.push(slide);
          } else if (!shouldBeVisible && isCurrentlyVisible) {
            const index = this.#visibleSlides.indexOf(slide);
            if (index > -1) {
              this.#visibleSlides.splice(index, 1);
            }
          }
        }

        this.#visibleSlides.sort((a, b) => slides.indexOf(a) - slides.indexOf(b));
        this.#updateVisibleSlides();
      },
      {
        root: scroller,
        threshold: SLIDE_VISIBLITY_THRESHOLD,
        // Add small margin to account for sub-pixel rendering
        rootMargin: '1px',
      }
    );

    // Observe all slides - observer will fire initial callback asynchronously
    slides.forEach((slide) => {
      this.#intersectionObserver?.observe(slide);
    });
  }

  /**
   * Centers the selected thumbnail in the thumbnails container
   * @param {number} index - The index of the selected thumbnail
   * @param {ScrollBehavior} [behavior] - The scroll behavior.
   */
  #centerSelectedThumbnail(index, behavior = 'smooth') {
    const selectedThumbnail = this.refs.thumbnails?.[index];
    if (!selectedThumbnail) return;

    const { thumbnailsContainer } = this.refs;
    if (!thumbnailsContainer || !(thumbnailsContainer instanceof HTMLElement)) return;

    const { slideshowControls } = this.refs;
    if (!slideshowControls || !(slideshowControls instanceof HTMLElement)) return;

    scrollIntoView(selectedThumbnail, {
      ancestor: thumbnailsContainer,
      behavior,
      block: 'center',
      inline: 'center',
    });
  }

  /**
   * Card galleries can start with most slides `hidden` (variant markup), so `connectedCallback` uses
   * `this.slides` and skips `Scroller`. When a combined listing reveals many slides client-side, upgrade once.
   */
  #ensureMultiSlideSetup() {
    if (this.#scroll) return;
    const slideCount = this.slides?.length || 0;
    if (slideCount <= 1) return;
    this.#initComplete = false;
    this.#setupSlideshow();
  }

  #applyInitialSlideSelection() {
    if (!this.#scroll || !this.refs.slides?.length) return;

    const index = clamp(this.initialSlideIndex, 0, this.refs.slides.length - 1);
    const slide = this.refs.slides[index];
    const slideId = slide?.getAttribute('slide-id');
    const selectOptions = { animate: false, scrollViewport: false };

    if (slideId) {
      void this.select({ id: slideId }, undefined, selectOptions);
      return;
    }

    void this.select(index, undefined, selectOptions);
  }

  syncScrollToCurrent() {
    const slide = this.slides?.[this.current];
    if (!slide || !this.#scroll) return;
    void this.#scroll.to(slide, { instant: true });
  }

  /**
   * Re-run observers after `slideshow-slide` nodes are added or removed (e.g. combined-listing card gallery).
   * @param {{ preserveSelection?: boolean }} [options]
   * @returns {Promise<void>}
   */
  refreshSlideObservers(options = {}) {
    const { preserveSelection = false } = options;
    if (!this.isConnected) return Promise.resolve();

    const hadScrollBefore = Boolean(this.#scroll);

    this.#ensureMultiSlideSetup();
    this.#updateScrollTrackingMode();
    const slideCount = this.refs.slides?.length ?? 0;

    return new Promise((resolve) => {
      if (slideCount > 1 && this.#scroll) {
        this.#setupIntersectionObserver();
        requestAnimationFrame(() => {
          this.#updateScrollerHeightForVerticalMode();
          this.#updateScrollTrackingMode();
          this.#updateVisibleSlides();
          this.#updateControlsVisibility();
          if (hadScrollBefore) {
            if (preserveSelection) {
              this.syncScrollToCurrent();
            } else {
              this.#applyInitialSlideSelection();
            }
          }
          resolve();
        });
      } else {
        requestAnimationFrame(() => {
          this.#updateVisibleSlides();
          resolve();
        });
      }
    });
  }

  #updateVisibleSlides() {
    const { slides } = this;
    if (!slides || !slides.length) return 0;

    const visibleSlides = this.visibleSlides;

    // Batch writes to the DOM
    scheduler.schedule(() => {
      // Update aria-hidden based on visibility
      slides.forEach((slide) => {
        const isVisible = visibleSlides.includes(slide);
        slide.setAttribute('aria-hidden', `£{!isVisible}`);
      });
    });

    return visibleSlides.length;
  }
}

if (!customElements.get('slideshow-component')) {
  customElements.define('slideshow-component', Slideshow);
}
